import { LoadingRows } from "../UI/Loading/LoadingRows.tsx";
import { BookedClassRoomDto } from "./types.ts";
import { FranchiseeItemFragment } from "../gql/graphql.ts";
import { useClassRoomSelectionHistory } from "./useClassRoomSelectionHistory.ts";
import { useEffect, useState } from "react";
import dayjs from "dayjs";
import { ClassRoomDetailScreenPath } from "../ClassRoom/ClassRoomDetailScreen.tsx";
import { useUserGroupName } from "../UserGroup/useUserGroupName.ts";
import { ClassRoomSelectionHistory } from "./ClassRoomSelectionHistory.tsx";

function TeacherRoleBadge({ role }: { role: "assigned" | "candidate" | null }) {
  if (role === "assigned")
    return (
      <span
        className={
          "text-xs px-1.5 py-0.5 rounded-full bg-primary bg-opacity-15 text-primary font-medium"
        }
      >
        assigned
      </span>
    );
  if (role === "candidate")
    return (
      <span
        className={
          "text-xs px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium"
        }
      >
        candidate
      </span>
    );
  return null;
}

function ClassRoomDetail({
  classRoom,
  teacherName,
  teacherById,
  selectedTeacherId,
  candidateClassRoomIds,
}: {
  classRoom: BookedClassRoomDto;
  teacherName: string;
  teacherById: Map<string, FranchiseeItemFragment>;
  selectedTeacherId?: string | null;
  candidateClassRoomIds?: Set<string>;
}) {
  const [expanded, setExpanded] = useState(false);
  const { fetchHistory, history, loading } = useClassRoomSelectionHistory();
  const getGroupName = useUserGroupName();

  useEffect(() => {
    if (expanded) fetchHistory(classRoom.classRoomId);
  }, [expanded, classRoom.classRoomId, fetchHistory]);

  const teacherRole: "assigned" | "candidate" | null = selectedTeacherId
    ? classRoom.teacherId === selectedTeacherId
      ? "assigned"
      : candidateClassRoomIds?.has(classRoom.classRoomId)
        ? "candidate"
        : null
    : null;

  return (
    <div className={"border border-slate-200 rounded-lg overflow-hidden"}>
      <button
        onClick={() => setExpanded((v) => !v)}
        className={
          "w-full flex items-center justify-between p-3 hover:bg-slate-50 transition-colors"
        }
      >
        <div className={"flex flex-col items-start gap-0.5"}>
          <span className={"flex items-center gap-2"}>
            <span className={"font-medium text-sm"}>{teacherName}</span>
            <TeacherRoleBadge role={teacherRole} />
          </span>
          <span className={"text-xs text-slate-400"}>
            {classRoom.is1on1 ? "1-to-1" : "Group"} ·{" "}
            {getGroupName(classRoom.userGroupId)}
          </span>
        </div>
        <div className={"flex items-center gap-3"}>
          <span className={"text-xs text-slate-500"}>
            {dayjs(classRoom.startUtc).format("HH:mm")} –{" "}
            {dayjs(classRoom.endUtc).format("HH:mm")}
          </span>
          <a
            href={ClassRoomDetailScreenPath.replace(
              ":classRoomId",
              classRoom.classRoomId,
            )}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => e.stopPropagation()}
            className={"text-xs text-primary underline hover:brightness-110"}
          >
            Details
          </a>
          <span className={"text-slate-400 text-xs"}>
            {expanded ? "▲" : "▼"}
          </span>
        </div>
      </button>

      {expanded && (
        <div className={"px-3 pb-3 border-t border-slate-100"}>
          {loading ? (
            <div className={"pt-2"}>
              <LoadingRows rows={3} />
            </div>
          ) : !history || history.length === 0 ? (
            <p className={"text-xs text-slate-400 italic pt-2"}>
              No selection data (pre-feature classroom)
            </p>
          ) : (
            <ClassRoomSelectionHistory
              history={history}
              teacherById={teacherById}
            />
          )}
        </div>
      )}
    </div>
  );
}

function AvailableTeachers({
  teacherIds,
  teacherById,
}: {
  teacherIds: string[];
  teacherById: Map<string, FranchiseeItemFragment>;
}) {
  if (teacherIds.length === 0) return null;
  const names = teacherIds
    .map((id) => {
      const teacher = teacherById.get(id);
      return {
        id,
        name: teacher ? `${teacher.givenName} ${teacher.familyName}` : id,
      };
    })
    .sort((a, b) => a.name.localeCompare(b.name));
  return (
    <div className={"space-y-2"}>
      <p className={"text-xs text-slate-400 font-medium uppercase"}>
        Available, not booked ({names.length})
      </p>
      <div className={"space-y-1"}>
        {names.map((t) => (
          <div
            key={t.id}
            className={
              "flex items-center justify-between border border-slate-200 border-dashed rounded-lg px-3 py-2 bg-slate-50"
            }
          >
            <span className={"text-sm text-slate-400"}>{t.name}</span>
            <span
              className={
                "text-xs px-1.5 py-0.5 rounded-full bg-slate-200 text-slate-500 font-medium"
              }
            >
              available
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function SlotDetailPanel({
  group,
  teacherById,
  onClose,
  selectedTeacherId,
  candidateClassRoomIds,
}: {
  group: { classRooms: BookedClassRoomDto[]; availableTeacherIds: string[] };
  teacherById: Map<string, FranchiseeItemFragment>;
  onClose: () => void;
  selectedTeacherId?: string | null;
  candidateClassRoomIds?: Set<string>;
}) {
  return (
    <div className={"fixed inset-0 z-50 flex items-center justify-center p-4"}>
      <div
        className={"absolute inset-0 bg-black bg-opacity-30"}
        onClick={onClose}
      />
      <div
        className={
          "relative bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[80vh] flex flex-col"
        }
      >
        <div
          className={
            "flex items-center justify-between px-4 py-3 border-b border-slate-200"
          }
        >
          <h2 className={"font-bold text-lg"}>
            {group.classRooms.length}{" "}
            {group.classRooms.length === 1 ? "classroom" : "classrooms"} in this
            slot
            {group.availableTeacherIds.length > 0 && (
              <span className={"font-normal text-sm text-slate-400"}>
                {" "}
                · {group.availableTeacherIds.length} available
              </span>
            )}
          </h2>
          <button
            onClick={onClose}
            className={
              "text-slate-400 hover:text-slate-600 text-xl leading-none"
            }
          >
            ×
          </button>
        </div>
        <div className={"overflow-y-auto p-4 space-y-3"}>
          {group.classRooms.map((c) => {
            const teacher = teacherById.get(c.teacherId);
            const teacherName = teacher
              ? `${teacher.givenName} ${teacher.familyName}`
              : c.teacherId;
            return (
              <ClassRoomDetail
                key={c.classRoomId}
                classRoom={c}
                teacherName={teacherName}
                teacherById={teacherById}
                selectedTeacherId={selectedTeacherId}
                candidateClassRoomIds={candidateClassRoomIds}
              />
            );
          })}
          <AvailableTeachers
            teacherIds={group.availableTeacherIds}
            teacherById={teacherById}
          />
        </div>
      </div>
    </div>
  );
}
